import { PropsWithChildren, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import ReactCodeInput from 'react-code-input';

import { useScreenSize } from '@/ui/hooks/useScreenSize';
import type { ScreenSize } from '@/ui/hooks/useScreenSize';

import { useAppSelector } from '@/ui/store/helperRedux';

import UserController from '@/controllers/UserController';
import { translate } from '@/domain/utils/translate';
import Icon from '@/ui/assets/Icon';

import BackClose from '@/ui/components/BackClose';
import CloseIcon from '@/ui/assets/CloseIcon';
import TextAndLink from '@/ui/components/TextAndLink';


interface PropsEmailRecoveryCode {
  setAuthCode: (code: string) => void;
  email: string;
  handleShowEmailRecoveryCode: () => void;
}

/** Componente formulario para validar código para cambio de contraseña
 *  @component
 */
const EmailRecoveryCode = ({
  setAuthCode,
  email,
  handleShowEmailRecoveryCode,
}: PropsWithChildren<PropsEmailRecoveryCode>): JSX.Element => {

  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido  */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();

  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = width < maxLargeWidth;

  /** Controlador para usuario */
  const { validateAuthCode, getAuthCode } = UserController();

  /** Status de usuario redux */
  const userLogued = useAppSelector((state) => state.userState);

  const [code, setCode] = useState<string>('');
  const [isLinkDisabled, setIsLinkDisabled] = useState<boolean>(false);
  const [inputKey, setInputKey] = useState<number>(0);

  const [messageError, setMessageError] = useState<string>('');
  const [messageSentMessage, setMessageSentMessage] = useState<string>('');

  /** Declaración inicial de los botones como deshabilitado */
  const [stateButton, setStateButton] = useState<string>('btn-primary-text-standard disabled');
  /** Habilita un botón para avanzar a la siguiente fase */
  const enableButton = (): void => setStateButton('btn-primary-text-standard');
  /** Des-habilita un botón para avanzar a la siguiente fase */
  const disableButton = (): void => setStateButton('btn-primary-text-standard disabled');

  const handleCodeChange = (value: string) => {
    setCode(value);
    if (value.length === 4) {
      enableButton();
      setMessageError('');
    } else {
      disableButton();
      setMessageError('');
    }
  };

  /** Validación del código de autorización */
  const handleValidateCode = async () => {
    disableButton();

    try {
      const validateCode = await validateAuthCode(userLogued.user.email ?? email, code);
      if (validateCode) {
        setAuthCode(code);
      } else {
        setMessageError(INVALIDATE_CODE);
        setMessageSentMessage(''); // Limpiar mensaje de éxito si hay error
      }
    } catch (error) {
      console.error(error);
    }

    if (!Error) {
      setMessageError(''); // Limpiar cualquier mensaje de error
      enableButton();
    }
  };

  /** Solicita código de autorización para cambio de contraseña */
  const handleGetCode = async (): Promise<void> => {
    try {
      setIsLinkDisabled(true);
      setCode(''); // Limpiar el input del código
      setInputKey(prev => prev + 1); // Forzar actualización del input
      disableButton(); // Deshabilitar el botón de continuar
      const result = await getAuthCode(userLogued.user.email ?? '', email ?? '');
      if (result) {
        // Limpiar mensaje de error y mostrar mensaje de éxito
        setMessageError('');
        setMessageSentMessage(SENT_CODE);

        setTimeout(() => {
          setMessageSentMessage('');
          setIsLinkDisabled(false);
        }, 5000);
      } else {
        setMessageError(SENT_CODE_ERROR);
        setMessageSentMessage('');
        setIsLinkDisabled(false);
      }
    } catch (error) {
      setMessageError(SENT_CODE_ERROR);
      setMessageSentMessage('');
      setIsLinkDisabled(false);
    }
  };

  /** Constantes para labels */
  const INVALIDATE_CODE = translate('session.invalidate_code');
  const LABEL_TEXT = translate('session.label_text');
  const LINK_LABEL_TEXT = translate('session.link_label_text');
  const RECOVERY_TITLE: string = translate('session.recovery_title');
  const CONTINUE_TEXT: string = translate('session.verify_text');
  const EMAIL_TEXT: string = translate('session.send_code_text').replace('EMAIL', email ?? '');
  const SENT_CODE: string = translate('session.sent_code');
  const SENT_CODE_ERROR: string = translate('session.sent_code_error');

  return (
    <Modal
      show={true}
      size={isMobile ? 'sm' : 'lg'}
      animation={false}
      dialogClassName={isMobile ? 'modal-bottom ' : 'modal-dialog-centered custom-modal-left'}
      contentClassName={isMobile ? 'border border-0 rounded-5 rounded-bottom-0 modal-slide-up' : '  rounded-4'}
    >
      <Modal.Body>
        <BackClose classMain='justify-content-end py-2'>
          <CloseIcon
            onClick={() => {
              handleShowEmailRecoveryCode();
            }}
            classMain='btn-icon-light'
          />
        </BackClose>
        <Row>
          <Col lg={12} className='d-flex justify-content-center flex-column my-5'>
            <Col className='mb-3'>
              <span className='d-flex justify-content-center align-items-center'>
                <Icon name='ico-send-email'></Icon>
                <span className={`${isMobile ? 'h3 m-0' : 'h2 m-0 '} fw-bold text-secondary-1 px-3 `}>{RECOVERY_TITLE}</span>
              </span>
            </Col>

            <Col className='d-flex justify-content-center'>
              <Col lg={6} className='d-flex justify-content-center flex-column px-4'>
                <span className='text-center text-secondary-2 paragraph '>{EMAIL_TEXT}</span>
                <div>
                  <ReactCodeInput
                    key={`code-${inputKey}`}
                    name='recoveryCode'
                    inputMode='numeric'
                    type='number'
                    fields={4}
                    value={code}
                    onChange={handleCodeChange}
                    className='d-flex justify-content-center mt-4'
                    inputStyle={{
                      margin: '0 5px',
                      width: '48px',
                      height: '54px',
                      textAlign: 'center',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      transition: 'border-color 0.3s',
                      outline: 'none',
                    }}
                  />
                </div>
                <Col xs={12} className='mt-2'>
                  <TextAndLink
                    text={LABEL_TEXT}
                    linkText={LINK_LABEL_TEXT}
                    onClick={handleGetCode}
                    classText='text-center p-regular'
                    separated={false}
                    classLinkButton={`link-text-standard text-tertiary-4 p-regular fw-bold px-3 ${isLinkDisabled ? 'disabled' : ''}`}
                    disabled={isLinkDisabled}
                  />
                </Col>
                <Col className=' mt-3 mb-3 px-2 text-danger p-regular'>
                  <span>{messageError}</span>
                </Col>
                {messageSentMessage && (
                  <Col className='mb-3 px-2 text-success p-regular'>
                    <span>{messageSentMessage}</span>
                  </Col>
                )}

                <div className='d-grid gap-2 mt-2 '>
                  <Button className={stateButton} type='button' onClick={handleValidateCode}>
                    <span className='p-large '>{CONTINUE_TEXT}</span>
                  </Button>
                </div>
              </Col>
            </Col>
          </Col>
        </Row>
      </Modal.Body>
      <Col xs={12} className='align-self-end pt-2'>
        <div className={'bg-primary-3 pt-2 rounded-top-0 '} />
      </Col>
    </Modal>
  );
};

export default EmailRecoveryCode;
